import React, { lazy, Suspense, useState, useEffect } from 'react';
import { Home, Search, ShoppingBag, User, Crosshair } from '../../icons/inline';
import { useStore } from '@nanostores/react';
import { cartItemCount } from '@agentic/magento-client/stores/cart';
import { quoteItemCount, openQuoteCart, initQuoteCart } from '@agentic/magento-client/stores/quote';
import { isB2bQuoteEnabled, isB2bHideCart, isB2bHidePriceEnabled } from '@agentic/magento-client/lib/feature-flags';
import { openAuthModal } from '../account/authModalStore';
import { urlFor } from '@agentic/magento-client/lib/url-for';
import { formatPrice } from '@agentic/magento-client/lib/utils';
import { b2bFlagsStore, requestB2bFlags } from '@agentic/magento-client/lib/b2b-product-flags';
import { searchPricePresentation } from '../search/search-price-policy';
const MobileAppDrawer = lazy(() => import('./MobileAppDrawer'));

export const MobileAppShell: React.FC<{ hasFinder?: boolean }> = ({ hasFinder = false }) => {
    const [activeTab, setActiveTab] = useState('home');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerRequested, setDrawerRequested] = useState(false);
    const [drawerContent, setDrawerContent] = useState<React.ReactNode>(null);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        // Hydrate the quote cart from localStorage so the badge survives reloads
        // (same bootstrap the desktop MiniQuoteCart icon performs).
        initQuoteCart();
    }, []);

    // Real cart count from nanostores
    const itemCount = useStore(cartItemCount) || 0;
    const quoteCount = useStore(quoteItemCount) || 0;
    // Smart visibility: the Quote tab only appears when the AgenticEcom_B2bSuite
    // (Request-a-Quote) module is installed + enabled — synced via global-data,
    // read with SSR⇄client parity (no extra Magento call). Non-B2B stores get the
    // standard 4-tab bar with no dead quote button.
    const quoteEnabled = isB2bQuoteEnabled();
    const cartHidden = isB2bHideCart();

    // Haptic Feedback helper
    const triggerHaptic = () => {
        if (typeof window !== 'undefined' && window.navigator?.vibrate) {
            window.navigator.vibrate(50);
        }
    };

    const openDrawer = (tab: string, content: React.ReactNode) => {
        triggerHaptic();
        setActiveTab(tab);
        if (tab === 'home' || tab === 'cart' || tab === 'quote') {
            setDrawerOpen(false);
            if (tab === 'cart') {
                // Trigger global cart events instead of inline drawer
                window.dispatchEvent(new Event('open-cart'));
            }
            if (tab === 'quote') {
                // QuoteDrawer is mounted globally in Layout.astro and listens to
                // the isQuoteDrawerOpen store — same path the desktop icon uses.
                openQuoteCart();
            }
            return;
        }

        setDrawerContent(content);
        setDrawerRequested(true);
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setActiveTab('home');
    };

    // Drawer content is wrapped at render time so we can inject an
    // unmistakable Close button at the top of the search drawer — Rick's
    // mobile QA flagged that once the search drawer opened, the only way to
    // dismiss it was tapping the dim overlay above (not obvious to a touch
    // user already focused on the input). The close button always sits in
    // the drawer header alongside the drag handle so dismiss is one tap.
    const wrappedDrawerContent = drawerContent ? (
        <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-1 pb-2">
                <span className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                    {activeTab === 'search' ? 'Search' : activeTab === 'account' ? 'Account' : ''}
                </span>
                <button
                    onClick={closeDrawer}
                    className="w-9 h-9 flex items-center justify-center rounded-full active:scale-90 transition-transform"
                    style={{
                        color: 'var(--text-primary)',
                        backgroundColor: 'var(--surface-2)',
                        border: '1px solid var(--border)',
                    }}
                    aria-label="Close"
                    type="button"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">{drawerContent}</div>
        </div>
    ) : null;

    return (
        <>
        {/* Mobile bottom bar — themes drive bg / border / icon-resting / icon-active
            via the Tier 2.7 --color-mobile-bottom-bar-* tokens. Each token has a
            sensible fallback so themes that haven't migrated still render. */}
        <div
            className="md:hidden fixed bottom-0 left-0 right-0 backdrop-blur-xl z-[100] pb-safe"
            style={{
                backgroundColor: 'var(--color-mobile-bottom-bar-bg, var(--surface-1))',
                borderTop: '1px solid var(--color-mobile-bottom-bar-border, var(--border))',
            }}
        >
            <div className="flex items-center justify-around h-[68px] px-2">

                <a
                    href="/"
                    onClick={() => triggerHaptic()}
                    className={`relative flex flex-col items-center justify-center flex-1 min-w-0 max-w-[72px] h-full transition-transform active:scale-90 ${activeTab === 'home' ? '' : ''}`}
                    style={{
                        color: activeTab === 'home'
                            ? 'var(--color-mobile-bottom-bar-icon-active, var(--accent-primary))'
                            : 'var(--color-mobile-bottom-bar-icon, var(--text-secondary))',
                    }}
                >
                    <Home className="w-6 h-6 mb-1" strokeWidth={activeTab === 'home' ? 2.5 : 2} />
                    <span className="text-[10px] font-medium text-[var(--color-mobile-bottom-bar-label,var(--color-mobile-bottom-bar-icon,var(--text-primary)))]">Home</span>
                </a>

                <button
                    onClick={() => openDrawer('search', <MobileSearchContent />)}
                    className={`relative flex flex-col items-center justify-center flex-1 min-w-0 max-w-[72px] h-full transition-transform active:scale-90 ${activeTab === 'search' ? '' : ''}`}
                    style={{
                        color: activeTab === 'search'
                            ? 'var(--color-mobile-bottom-bar-icon-active, var(--accent-primary))'
                            : 'var(--color-mobile-bottom-bar-icon, var(--text-secondary))',
                    }}
                >
                    <Search className="w-6 h-6 mb-1" strokeWidth={activeTab === 'search' ? 2.5 : 2} />
                    <span className="text-[10px] font-medium text-[var(--color-mobile-bottom-bar-label,var(--color-mobile-bottom-bar-icon,var(--text-primary)))]">Search</span>
                </button>

                {hasFinder && (
                    <button
                        onClick={() => {
                            triggerHaptic();
                            window.dispatchEvent(new CustomEvent('open-finder', { detail: { chainCode: null } }));
                        }}
                        className="relative flex flex-col items-center justify-center flex-1 min-w-0 max-w-[72px] h-full transition-transform active:scale-90"
                        style={{
                            color: 'var(--color-mobile-bottom-bar-icon, var(--text-secondary))',
                        }}
                        aria-label="Find what fits"
                    >
                        <Crosshair className="w-6 h-6 mb-1" strokeWidth={2} />
                        <span className="text-[10px] font-medium text-[var(--color-mobile-bottom-bar-label,var(--color-mobile-bottom-bar-icon,var(--text-primary)))]">Fits</span>
                    </button>
                )}

                {!cartHidden && <button
                    onClick={() => openDrawer('cart', null)}
                    className={`relative flex flex-col items-center justify-center flex-1 min-w-0 max-w-[72px] h-full transition-transform active:scale-90 ${activeTab === 'cart' ? '' : ''}`}
                    style={{
                        color: activeTab === 'cart'
                            ? 'var(--color-mobile-bottom-bar-icon-active, var(--accent-primary))'
                            : 'var(--color-mobile-bottom-bar-icon, var(--text-secondary))',
                    }}
                >
                    <div className="relative">
                        <ShoppingBag className="w-6 h-6 mb-1" strokeWidth={activeTab === 'cart' ? 2.5 : 2} />
                        {mounted && itemCount > 0 && (
                            <div className="absolute -top-1 -right-2 text-[var(--text-primary)] text-[10px] font-bold h-4 min-w-[16px] flex items-center justify-center rounded-full px-1 shadow-lg" style={{ backgroundColor: 'var(--accent-primary)', border: '1px solid var(--surface-1)' }}>
                                {itemCount}
                            </div>
                        )}
                    </div>
                    <span className="text-[10px] font-medium text-[var(--color-mobile-bottom-bar-label,var(--color-mobile-bottom-bar-icon,var(--text-primary)))]">Cart</span>
                </button>}

                {quoteEnabled && (
                <button
                    onClick={() => openDrawer('quote', null)}
                    className={`relative flex flex-col items-center justify-center flex-1 min-w-0 max-w-[72px] h-full transition-transform active:scale-90 ${activeTab === 'quote' ? '' : ''}`}
                    style={{
                        color: activeTab === 'quote'
                            ? 'var(--color-mobile-bottom-bar-icon-active, var(--accent-primary))'
                            : 'var(--color-mobile-bottom-bar-icon, var(--text-secondary))',
                    }}
                >
                    <div className="relative">
                        {/* Document/Quote icon — same glyph as the desktop MiniQuoteCart,
                            distinct from the shopping bag. */}
                        <svg className="w-6 h-6 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={activeTab === 'quote' ? 2.5 : 2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {mounted && quoteCount > 0 && (
                            <div className="absolute -top-1 -right-2 text-[var(--text-primary)] text-[10px] font-bold h-4 min-w-[16px] flex items-center justify-center rounded-full px-1 shadow-lg" style={{ backgroundColor: 'var(--accent-primary)', border: '1px solid var(--surface-1)' }}>
                                {quoteCount}
                            </div>
                        )}
                    </div>
                    <span className="text-[10px] font-medium text-[var(--color-mobile-bottom-bar-label,var(--color-mobile-bottom-bar-icon,var(--text-primary)))]">Quote</span>
                </button>
                )}

                <button
                    onClick={() => openDrawer('account', <MobileAccountContent />)}
                    className={`relative flex flex-col items-center justify-center flex-1 min-w-0 max-w-[72px] h-full transition-transform active:scale-90 ${activeTab === 'account' ? '' : ''}`}
                    style={{
                        color: activeTab === 'account'
                            ? 'var(--color-mobile-bottom-bar-icon-active, var(--accent-primary))'
                            : 'var(--color-mobile-bottom-bar-icon, var(--text-secondary))',
                    }}
                >
                    <User className="w-6 h-6 mb-1" strokeWidth={activeTab === 'account' ? 2.5 : 2} />
                    <span className="text-[10px] font-medium text-[var(--color-mobile-bottom-bar-label,var(--color-mobile-bottom-bar-icon,var(--text-primary)))]">Account</span>
                </button>

            </div>
        </div>

            {/* Slide-up Drawer */}
            <Suspense fallback={<span className="sr-only" role="status">Loading panel…</span>}>
                {drawerRequested && <MobileAppDrawer open={drawerOpen} onClose={closeDrawer}>{wrappedDrawerContent}</MobileAppDrawer>}
            </Suspense>
        </>
      );
};

interface MobileSearchProduct {
    uid?: string;
    name?: string;
    sku?: string;
    url_key?: string;
    small_image?: { url?: string };
    price_range?: { minimum_price?: { final_price?: { value?: number; currency?: string } } };
}

const MobileSearchContent = () => {
    const [query, setQuery] = React.useState('');
    const [results, setResults] = React.useState<MobileSearchProduct[]>([]);
    const [loading, setLoading] = React.useState(false);
    const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    // B2B hide-price overlay for the search-result prices (no-op off-B2B).
    const b2bFlags = useStore(b2bFlagsStore);
    React.useEffect(() => {
        const skus = results.map((r) => r.sku).filter(Boolean) as string[];
        if (skus.length) requestB2bFlags(skus);
    }, [results]);

    const doSearch = (q: string) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (!q.trim()) { setResults([]); setLoading(false); return; }
        setLoading(true);
        timerRef.current = setTimeout(() => {
            interface SearchResp { data?: { products?: { items?: MobileSearchProduct[] } } }
            const endpoint = typeof window !== 'undefined' ? `${window.location.origin}/api/graphql` : '/api/graphql';
            void fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    query: `{ products(search: "${q.trim()}", pageSize: 6) { items { uid name sku url_key small_image { url } price_range { minimum_price { final_price { value currency } } } } } }`,
                }),
            })
                .then(r => r.json() as Promise<SearchResp>)
                .then(json => {
                    setResults(json.data?.products?.items ?? []);
                    setLoading(false);
                })
                .catch(() => { setResults([]); setLoading(false); });
        }, 300);
    };

    return (
        <div className="text-[var(--text-primary)]">
            {/* Inset, thinner focus ring: the default global ring is a 3px OUTWARD
                box-shadow, so on a near-full-width mobile input it reached/clipped
                at the screen edges. Drawing it INSIDE the box (inset 2px) keeps the
                brand ring fully on-screen regardless of input width. */}
            <style dangerouslySetInnerHTML={{ __html: `
              .mobile-search-field:focus, .mobile-search-field:focus-visible {
                box-shadow: inset 0 0 0 2px var(--color-global-link-hover, var(--accent-primary, #FB7C00)) !important;
                border-color: var(--color-global-link-hover, var(--accent-primary, #FB7C00)) !important;
                outline: none !important;
              }
            ` }} />
            <h3 className="text-xl font-bold mb-4">Search Products</h3>
            <form onSubmit={(e) => { e.preventDefault(); if (query.trim()) window.location.href = `/search?q=${encodeURIComponent(query.trim())}`; }}>
                <input
                    type="text"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); doSearch(e.target.value); }}
                    placeholder="Search walkie-talkies, radios, accessories..."
                    // box-border: w-full + px-4 + the 1px border must stay INSIDE the
                    // drawer's padding. Without an explicit border-box, content-box
                    // sizing makes the input 100%+32px+2px wide → it overflows and the
                    // left/right borders get clipped off the narrow mobile screen.
                    className="mobile-search-field box-border w-full px-4 py-3 mb-4 outline-none"
                    // --border is undefined in light mode, so `1px solid var(--border)`
                    // was an invalid shorthand -> the browser dropped it -> border:none
                    // (visible in dark only, where --border IS defined). Use the dedicated
                    // input-border token, which is defined in both modes, with hard
                    // fallbacks so the border can never collapse to none again.
                    style={{ backgroundColor: 'var(--surface-2)', border: '1px solid var(--color-global-input-border, var(--glass-border, #E4E4E7))', color: 'var(--text-primary)', borderRadius: 'var(--style-btn-radius, 12px)' }}
                    autoFocus
                />
            </form>
            {loading && <div className="text-sm text-[var(--text-secondary)] mb-4">Searching...</div>}
            {results.length > 0 && (
                <div className="space-y-2">
                    {results.map((p: MobileSearchProduct) => (
                        <a key={p.uid} href={urlFor('product', p.url_key)} className="flex items-center gap-3 p-3 hover:border-[var(--color-global-link-hover)] transition-all" style={{ textDecoration: 'none', borderRadius: 'var(--style-card-radius, 12px)', backgroundColor: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                            {p.small_image?.url && <img src={p.small_image.url} alt={p.name} className="w-12 h-12 object-cover" style={{ borderRadius: 'var(--style-img-radius, 8px)', backgroundColor: 'var(--surface-2)' }} />}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[var(--text-primary)] truncate">{p.name}</p>
                                <p className="text-xs text-[var(--color-accent-fg)] font-bold">
                                    {(() => {
                                        const presentation = searchPricePresentation(
                                            p, p.sku ? b2bFlags[p.sku] : undefined, isB2bHidePriceEnabled(),
                                        );
                                        return presentation.label ?? formatPrice(presentation.value, presentation.currency);
                                    })()}
                                </p>
                            </div>
                        </a>
                    ))}
                    <a href={`/search?q=${encodeURIComponent(query)}`} className="block text-center text-sm text-[var(--color-accent-fg)] py-3 hover:underline">
                        View all results →
                    </a>
                </div>
            )}
            {!loading && query.trim() && results.length === 0 && (
                <p className="text-sm text-[var(--text-secondary)] text-center py-4">No results for "{query}"</p>
            )}
        </div>
    );
};

const MobileAccountContent = () => {
    // Static import of the store action (see ../account/authModalStore).
    // The previous dynamic import collided with Layout.astro's static
    // import of AuthModal — Vite warned that the dynamic boundary was
    // ineffective. The store module is ~200 bytes; no need to be lazy.
    const handleOpenAuth = () => { openAuthModal(); };

    // Logged-in awareness — mirrors UserLinks (localStorage customer_token +
    // the 'auth-change' event). WITHOUT this the drawer always showed
    // "Log In / Create Account", leaving a signed-in mobile user with no way
    // to reach their account pages or sign out (hover dropdowns don't exist on
    // touch). Now it shows the account menu when authenticated.
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [customerName, setCustomerName] = useState('');
    useEffect(() => {
        try {
            if (localStorage.getItem('customer_token')) {
                setIsLoggedIn(true);
                setCustomerName(localStorage.getItem('customer_name') ?? '');
            }
        } catch { /* SSR / storage blocked */ }
        const onAuth = (e: Event) => {
            const d = (e as CustomEvent<{ isLoggedIn?: boolean; name?: string }>).detail;
            setIsLoggedIn(!!d?.isLoggedIn);
            setCustomerName(d?.name ?? '');
        };
        window.addEventListener('auth-change', onAuth);
        return () => window.removeEventListener('auth-change', onAuth);
    }, []);

    const handleLogout = () => {
        try {
            localStorage.removeItem('customer_token');
            localStorage.removeItem('customer_name');
        } catch { /* ignore */ }
        window.dispatchEvent(new CustomEvent('auth-change', { detail: { isLoggedIn: false } }));
        window.location.href = '/';
    };

    if (isLoggedIn) {
        const links = [
            { href: '/account', label: 'My Account' },
            { href: '/account/orders', label: 'Orders' },
            { href: '/account/wishlist', label: 'Wishlist' },
        ];
        return (
            <div className="text-[var(--text-primary)]">
                <h3 className="text-xl font-bold mb-1">My Account</h3>
                {customerName && (
                    <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                        Signed in as {customerName.split(/\s+/)[0]}
                    </p>
                )}
                <nav className="flex flex-col">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            className="py-3 text-[15px] font-medium active:scale-[0.99] transition-transform"
                            style={{ color: 'var(--text-primary)', borderBottom: '1px solid var(--glass-border)', textDecoration: 'none' }}
                        >
                            {l.label}
                        </a>
                    ))}
                    <button
                        onClick={handleLogout}
                        className="py-3 text-left text-[15px] font-medium active:scale-[0.99] transition-transform"
                        style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                        Sign Out
                    </button>
                </nav>
            </div>
        );
    }

    return (
        <div className="text-[var(--text-primary)]">
            <h3 className="text-xl font-bold mb-4">My Account</h3>
            <div className="space-y-4">
                <button onClick={handleOpenAuth} className="w-full text-[var(--text-on-accent)] font-bold py-3 transition-all active:scale-95" style={{ borderRadius: 'var(--style-btn-radius, 12px)', backgroundColor: 'var(--accent-primary)' }}>
                    Log In
                </button>
                <button onClick={handleOpenAuth} className="w-full font-bold py-3 transition-all active:scale-95 text-[var(--text-primary)]" style={{ borderRadius: 'var(--style-btn-radius, 12px)', backgroundColor: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                    Create Account
                </button>
            </div>
        </div>
    );
};
